<template>
  <SDialog
    :show-dialog="showDialog"
    :width="450"
    :title="$t('account.createAccount')"
    @update:show-dialog="emits('update:showDialog', $event)"
    @close="emits('close')"
  >
    <VCardText class="px-6 pb-6 pt-2">
      <CmsContent
        class="font14 text-center mt-0 mb-6"
        :html-content="$t('ida.p.didntFind')"
      />

      <ObservedForm
        :loading="loading"
        :error-messages="errors"
        data-testid="join-registration-form"
        @submit="onSubmit"
      >
        <VRow no-gutters>
          <VCol cols="12">
            <TextFieldWithValidation
              v-model="firstName"
              :label="$t('label.firstName')"
              :rules="{ required: true, max: 30, su_cc: { reverse: true } }"
              name="firstName"
              required
              hide-details-without-error
              data-testid="join-reg-first-name"
            />
          </VCol>
        </VRow>
        <VRow no-gutters>
          <VCol cols="12">
            <TextFieldWithValidation
              v-model="lastName"
              :label="$t('label.lastName')"
              :rules="{ required: true, max: 30, su_cc: { reverse: true } }"
              name="lastName"
              required
              hide-details-without-error
              data-testid="join-reg-last-name"
            />
          </VCol>
        </VRow>
        <VRow no-gutters>
          <VCol cols="12">
            <TextFieldWithValidation
              v-model="email"
              :label="$t('label.email')"
              rules="required|email|max:75"
              name="email"
              required
              readonly
              hide-details-without-error
              data-testid="join-reg-email"
              autocomplete="username"
            />
          </VCol>
        </VRow>
        <VRow no-gutters>
          <VCol cols="12">
            <PasswordField
              v-model="password"
              name="regPassword"
              hide-details-without-error
              data-testid="join-reg-password"
              autocomplete="new-password"
            />
          </VCol>
        </VRow>
        <VRow no-gutters>
          <VCol cols="12">
            <PasswordField
              v-model="passwordConfirmation"
              name="confirmPwd"
              :alternate-label="$t('auth.label.confirmPwd')"
              alternate-rules="required|confirmed:@regPassword"
              hide-details-without-error
              data-testid="join-reg-password-confirmation"
              autocomplete="new-password"
            />
          </VCol>
        </VRow>
        <VRow no-gutters class="mt-6">
          <VCol cols="12">
            <SBtn
              color="primary"
              action
              type="submit"
              block
              data-testid="join-reg-submit"
            >
              {{ $t('account.createAccount') }}
            </SBtn>
          </VCol>
        </VRow>
      </ObservedForm>
    </VCardText>
  </SDialog>
</template>

<script lang="ts" setup>
import ObservedForm from '~/components/base/ObservedForm.vue'
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import PasswordField from '~/components/base/fields/PasswordField.vue'
import CmsContent from '~/components/content/CmsContent.vue'
import { useJoinStore } from '~/stores/join'
import { useAccountStore } from '~/stores/account'
import { useAuthStore } from '~/stores/auth'

interface Props {
  showDialog: boolean
}

defineProps<Props>()

const emits = defineEmits<{
  (e: 'update:showDialog', show: boolean): void
  (e: 'close'): void
  (e: 'authenticated', password: string): void
}>()

const joinStore = useJoinStore()
const accountStore = useAccountStore()
const authStore = useAuthStore()
const { processAction, errors, loading } = useAction()

const firstName = ref('')
const lastName = ref('')
const email = ref(joinStore.session.email)
const password = ref('')
const passwordConfirmation = ref('')

const onSubmit = () => {
  processAction(async () => {
    await accountStore.createAccount({
      customer: {
        firstName: firstName.value,
        lastName: lastName.value,
        email: email.value,
        password: password.value,
      },
    })
    await authStore.authenticate({
      username: email.value,
      password: password.value,
      persist: false,
    }, { skipWishlist: true })

    joinStore.setRecruitName(firstName.value, lastName.value)
    emits('authenticated', password.value)
  })
}
</script>
